import React, { useEffect, useState } from 'react';
import PatientAppointmentSummary from './PatientAppointmentSummary';
import PatientVisitInformation from './PatientVisitInformation';
import PaymentHistory from './PaymentHistory';
import { FaArrowLeft, FaSearchPlus, FaUser } from 'react-icons/fa';
import { Tag } from 'lucide-react';
import './PatientInfoPanel.css';
import PharmacyPaymentsForIP from './PharmacyPaymentsForIP';
import OngoingCharges from './OngoingCharges';
import ENDPOINTS, { API_BASE_URL } from '../../../Constants/ApiEndpoints';
import axios from 'axios';
import { toast } from 'react-toastify';
import PatientStickerLabel from '../../../CommonComponents/Utils/PatientStickerLabel';

const PatientInfoPanel = ({ patient, onBack }) => {
  const [prescriptionInvoices, setprescriptionInvoices] = useState([]);
  const [ipInvoices, setipInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [showLabelModal, setShowLabelModal] = useState(false);
  const initialFlagged = Boolean(patient?.flagged ?? patient?.isFlagged ?? false);
  const [isFlagged, setIsFlagged] = useState(initialFlagged);
  const [flagLoading, setFlagLoading] = useState(false);
  const [fullPatient, setFullPatient] = useState(patient || {});

  useEffect(() => {
    const activeFlag = patient?.flagged ?? patient?.isFlagged;
    if (activeFlag !== undefined) {
      setIsFlagged(Boolean(activeFlag));
    }
  }, [patient?.flagged, patient?.isFlagged]);

  const handleFlagClick = async () => {
    const mrNo = patient?.mrNumber || fullPatient?.mrNumber;
    if (!mrNo) return;
    const newFlagValue = !isFlagged;
    setFlagLoading(true);
    try {
      await axios.patch(
        `${API_BASE_URL}/flagThePatient/${mrNo}/${newFlagValue}`
      );
      setIsFlagged(newFlagValue);
      setFullPatient(prev => ({ ...prev, flagged: newFlagValue, isFlagged: newFlagValue }));
      if (patient) {
        patient.flagged = newFlagValue;
        patient.isFlagged = newFlagValue;
      }
      toast.success("Patient flag status updated!");
    } catch (err) {
      console.error("Failed to update flag:", err);
      toast.error("Failed to update flag status.");
    } finally {
      setFlagLoading(false);
    }
  };

  const [fetchSummaryFn, setFetchSummaryFn] = useState(() => () => { });

  const handleSetFetchSummary = (fn) => {
    setFetchSummaryFn(() => fn);
  };

  const fetchPrescriptionInvoices = async () => {
    try {
      const response = await axios.get(ENDPOINTS.GET_PRESCRIPTION_INVOICES_OF_AN_ADMISSION, {
        params: { mrNumber: patient?.mrNumber }
      });
      setprescriptionInvoices(response.data);
    } catch (err) {
      setError(err.message || "Failed to fetch invoices");
    }
  };

  const fetchInvoices = async () => {
    if (!patient?.admissionId) return;
    try {
      const response = await axios.get(`${API_BASE_URL}/getAllInvoicesOfAnAdmission/admission/${patient.admissionId}`);
      setipInvoices(response.data);
    } catch (err) {
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const fetchAdmissionDetails = async () => {
    if (!patient?.admissionId) return;
    try {
      const response = await axios.get(`${API_BASE_URL}/getAdmissionDetailsByAdmissionId/${patient.admissionId}`);
      if (response.data) {
        setFullPatient(prev => ({ ...prev, ...response.data }));
        const flagVal = response.data.flagged ?? response.data.isFlagged;
        if (flagVal !== undefined) {
          setIsFlagged(Boolean(flagVal));
        }
      }
    } catch (err) {
      console.error("Error fetching admission details:", err);
    }
  };

  useEffect(() => {
    setprescriptionInvoices([]);
    setipInvoices([]);
    setLoading(true);
    setError(null);
    setFullPatient(patient || {});

    if (patient?.mrNumber) {
      fetchPrescriptionInvoices();
    }
    if (patient?.admissionId) {
      fetchInvoices();
      fetchAdmissionDetails();
    } else {
      setLoading(false);
    }
  }, [patient?.admissionId, patient?.mrNumber]);

  const formatPatientTitle = () => {
    const name = patient?.patientName || patient?.name || 'Patient Details';
    const gender = patient?.gender ? `, ${patient.gender}` : '';
    const age = patient?.age ? `, ${patient.age} yrs` : '';
    return `${name}${gender}${age}`;
  };

  return (
    <div className="patient-info-wrapper">
      <div className="breadcrumbb">
        <div className="breadcrumbb-title">
          Dashboard / <strong>Admissions / Patient Info</strong>
        </div>
        <button onClick={onBack} className="custom-back-button">
          <FaArrowLeft className="icon-left" />
          Back to Admissions
        </button>
      </div>

      {/* Enterprise Unified Patient Hero Card */}
      <div className="patient-hero-card">
        <div className="patient-card-accent-bar" />

        {/* Hero Header Banner: Circular Photo Avatar + Patient Info & Badges */}
        <div className="patient-hero-header">
          <div
            className="patient-avatar-wrapper clickable-avatar"
            onClick={() => setShowPhotoModal(true)}
            style={{ position: 'relative', cursor: 'pointer' }}
            title="Click to view full picture"
          >
            {patient?.photo ? (
              <img
                src={`data:image/jpeg;base64,${patient.photo}`}
                alt="Patient"
                className="patient-hero-avatar"
                loading="lazy"
              />
            ) : (
              <div className="patient-avatar-placeholder">
                <FaUser size={40} color="#94a3b8" />
              </div>
            )}
            <div className="avatar-hover-overlay">
              <FaSearchPlus size={20} color="#ffffff" />
            </div>
          </div>

          <div className="patient-identity-block">
            <div className="patient-title-row">
              <h2 className="patient-hero-name">{patient?.patientName || patient?.name || 'Patient Name'}</h2>
              {patient?.gender && <span className="hero-meta-badge">{patient.gender}</span>}
              {patient?.age && <span className="hero-meta-badge">{patient.age} yrs</span>}
            </div>

            <div className="patient-sub-badges">
              <button
                className="priority-flag-btn"
                onClick={handleFlagClick}
                disabled={flagLoading}
                style={{
                  backgroundColor: isFlagged ? "#dc2626" : "#16a34a",
                }}
              >
                {flagLoading ? "Processing..." : isFlagged ? "🚨 Flagged" : "🚨 Flag"}
              </button>
            </div>
          </div>

          <div className="hero-right-actions">
            <button
              className="print-label-btn"
              onClick={() => setShowLabelModal(true)}
              title="Print Sticker Label for Patient File"
            >
              <Tag size={14} />
              <span>Label</span>
            </button>
            {patient?.emergency && (
              <span className="badge-pill badge-priority">
                🚨 Emergency
              </span>
            )}
          </div>
        </div>

        {/* Content Body Grid */}
        <div className="profile-content-grid">
          <PatientAppointmentSummary patient={fullPatient} />
          <PatientVisitInformation patient={fullPatient} />
        </div>
      </div>

      <div className="payment-history-section">
        <PaymentHistory
          patient={patient}
          ipInvoices={ipInvoices}
          fetchInvoices={fetchInvoices}
          error={error}
          loading={loading}
          onPaymentSubmit={() => {
            if (fetchSummaryFn) fetchSummaryFn();
          }}
        />

        <OngoingCharges
          patient={patient}
          fetchInvoices={fetchInvoices}
          registerFetchSummary={handleSetFetchSummary}
          prescriptionInvoices={prescriptionInvoices}
          ipInvoices={ipInvoices}
        />
        <PharmacyPaymentsForIP
          patient={patient}
          prescriptionInvoices={prescriptionInvoices}
          loading={loading}
          error={error}
        />
      </div>

      {/* Reusable Patient File Sticker Label Modal */}
      <PatientStickerLabel
        isOpen={showLabelModal}
        onClose={() => setShowLabelModal(false)}
        patient={fullPatient}
      />

      {/* Fullscreen Photo Lightbox Modal */}
      {showPhotoModal && (
        <div className="photo-lightbox-backdrop" onClick={() => setShowPhotoModal(false)}>
          <div className="photo-lightbox-modal" onClick={(e) => e.stopPropagation()}>
            <button className="photo-lightbox-close" onClick={() => setShowPhotoModal(false)}>&times;</button>
            <img
              src={patient?.photo ? `data:image/jpeg;base64,${patient.photo}` : 'https://static.vecteezy.com/system/resources/thumbnails/055/630/851/small/male-nurse-or-doctor-avatar-icon-wearing-medical-uniform-vector.jpg'}
              alt={patient?.patientName || 'Patient'}
              className="photo-lightbox-img"
            />
            <div className="photo-lightbox-caption">
              <strong>{patient?.patientName || 'Patient'}</strong> (MR: {patient?.mrNumber || 'N/A'})
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientInfoPanel;
